import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import OnboardingShell from "./OnboardingShell";
import { FONTS, GLASS_SX } from "../constants/styles";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ColoredIcon from "./ColoredIcon";
import DefaultIcon from "../img/default_icon.svg";

const BENEFITS = [
    { text: "No coding or design skills needed" },
    { text: "Bookings & payments included" },
    { text: "Completely free" },
];

const StartScreen = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    return (
        <OnboardingShell variant="celebration" showLogo>
            <Stack
                className="force-dynamic-translation"
                flex={1}
                width="100%"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    px: 3,
                    boxSizing: "border-box",
                    pb: "calc(env(safe-area-inset-bottom) + 16px)",
                    pt: "clamp(0px, 1vh, 8px)",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* ─── Ambient light orbs ─── */}
                <Box
                    sx={{
                        position: "absolute",
                        width: 200,
                        height: 200,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
                        top: "8%",
                        right: "-15%",
                        pointerEvents: "none",
                        "@keyframes orbDrift1": {
                            "0%, 100%": { transform: "translate(0, 0)" },
                            "50%": { transform: "translate(-10px, 8px)" },
                        },
                        animation: "orbDrift1 8s ease-in-out infinite",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        width: 160,
                        height: 160,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)",
                        bottom: "20%",
                        left: "-10%",
                        pointerEvents: "none",
                        "@keyframes orbDrift2": {
                            "0%, 100%": { transform: "translate(0, 0)" },
                            "50%": { transform: "translate(8px, -6px)" },
                        },
                        animation: "orbDrift2 10s ease-in-out infinite",
                    }}
                />

                {/* ─── Top: Icon + Headline + Benefits ─── */}
                <Stack
                    alignItems="center"
                    spacing={0}
                    sx={{ position: "relative", zIndex: 1 }}
                >
                    {/* Icon with radial light */}
                    <Box
                        sx={{
                            position: "relative",
                            mb: "clamp(12px, 2.5vh, 24px)",
                            "@keyframes heroEnter": {
                                "0%": {
                                    opacity: 0,
                                    transform: "scale(0.7) translateY(24px)",
                                },
                                "100%": {
                                    opacity: 1,
                                    transform: "scale(1) translateY(0)",
                                },
                            },
                            animation:
                                "heroEnter 0.7s cubic-bezier(0.16, 1, 0.3, 1)",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                width: "clamp(180px, 30vh, 260px)",
                                height: "clamp(180px, 30vh, 260px)",
                                transform: "translate(-50%, -50%)",
                                borderRadius: "50%",
                                background:
                                    "radial-gradient(circle, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.04) 40%, transparent 70%)",
                                pointerEvents: "none",
                                "@keyframes lightBreathe": {
                                    "0%, 100%": {
                                        opacity: 0.8,
                                        transform:
                                            "translate(-50%, -50%) scale(1)",
                                    },
                                    "50%": {
                                        opacity: 1,
                                        transform:
                                            "translate(-50%, -50%) scale(1.08)",
                                    },
                                },
                                animation:
                                    "lightBreathe 4s ease-in-out infinite",
                            }}
                        />
                        <Box
                            sx={{
                                width: "clamp(76px, 12vh, 112px)",
                                height: "clamp(76px, 12vh, 112px)",
                                borderRadius: "clamp(20px, 3.5vh, 30px)",
                                ...GLASS_SX,
                                bgcolor: "rgba(255,255,255,0.14)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow:
                                    "0 16px 48px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)",
                                position: "relative",
                                zIndex: 1,
                                "@keyframes gentleFloat": {
                                    "0%, 100%": {
                                        transform: "translateY(0)",
                                    },
                                    "50%": {
                                        transform: "translateY(-6px)",
                                    },
                                },
                                animation:
                                    "gentleFloat 4s ease-in-out 0.7s infinite",
                            }}
                        >
                            <ColoredIcon
                                icon={DefaultIcon}
                                size={60}
                                color="white"
                            />
                        </Box>
                    </Box>

                    {/* Headline */}
                    <Typography
                        sx={{
                            fontFamily: FONTS.display,
                            fontWeight: 700,
                            fontSize: "clamp(26px, 4vh, 32px)",
                            color: "white",
                            textAlign: "center",
                            lineHeight: 1.15,
                            letterSpacing: "-0.5px",
                            mb: "clamp(6px, 1vh, 12px)",
                            maxWidth: 340,
                            "@keyframes headlineIn": {
                                from: {
                                    opacity: 0,
                                    transform: "translateY(16px)",
                                },
                                to: {
                                    opacity: 1,
                                    transform: "translateY(0)",
                                },
                            },
                            animation:
                                "headlineIn 0.5s ease-out 0.15s both",
                        }}
                    >
                        You deserve your own app
                    </Typography>

                    {/* Subtitle */}
                    <Typography
                        sx={{
                            fontFamily: FONTS.body,
                            fontSize: "15px",
                            color: "rgba(255,255,255,0.6)",
                            textAlign: "center",
                            lineHeight: 1.5,
                            mb: "clamp(12px, 2vh, 20px)",
                            "@keyframes subtitleIn": {
                                from: { opacity: 0 },
                                to: { opacity: 1 },
                            },
                            animation: "subtitleIn 0.5s ease-out 0.3s both",
                        }}
                    >
                        AI creates it in 30 seconds
                    </Typography>

                    {/* Benefits */}
                    <Stack
                        spacing={0.75}
                        width="100%"
                        maxWidth={280}
                        sx={{
                            "@keyframes benefitsIn": {
                                from: {
                                    opacity: 0,
                                    transform: "translateY(12px)",
                                },
                                to: {
                                    opacity: 1,
                                    transform: "translateY(0)",
                                },
                            },
                            animation:
                                "benefitsIn 0.5s ease-out 0.45s both",
                        }}
                    >
                        {BENEFITS.map((b, i) => (
                            <Stack
                                key={i}
                                direction="row"
                                alignItems="center"
                                spacing={1.25}
                                sx={{
                                    py: "clamp(4px, 0.8vh, 8px)",
                                    px: 1.5,
                                    borderRadius: "12px",
                                    bgcolor: "rgba(255,255,255,0.08)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                }}
                            >
                                <CheckRoundedIcon
                                    sx={{
                                        fontSize: 14,
                                        color: "rgba(255,255,255,0.6)",
                                        flex: "0 0 auto",
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontFamily: FONTS.body,
                                        fontSize: "13.5px",
                                        fontWeight: 500,
                                        color: "rgba(255,255,255,0.8)",
                                        letterSpacing: "0.1px",
                                    }}
                                >
                                    {b.text}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>

                {/* ─── Bottom: CTA + Secondary ─── */}
                <Stack
                    alignItems="center"
                    width="100%"
                    spacing="clamp(8px, 1.2vh, 16px)"
                    sx={{ position: "relative", zIndex: 1 }}
                >
                    {/* CTA with shimmer */}
                    <Box
                        role="button"
                        onClick={() => {
                            sessionStorage.removeItem("brandCreated");
                            setTimeout(() => {
                                window.location.href = "/creation/business";
                            }, 100);
                        }}
                        sx={{
                            width: "100%",
                            maxWidth: 340,
                            cursor: "pointer",
                            userSelect: "none",
                            borderRadius: "18px",
                            bgcolor: "white",
                            position: "relative",
                            overflow: "hidden",
                            transition:
                                "transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease",
                            boxShadow:
                                "0 4px 24px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.15)",
                            "&:hover": {
                                transform: "translateY(-3px) scale(1.01)",
                                boxShadow:
                                    "0 12px 40px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.2)",
                            },
                            "&:active": {
                                transform: "translateY(0) scale(0.98)",
                            },
                            "@keyframes ctaEnter": {
                                "0%": {
                                    opacity: 0,
                                    transform: "translateY(20px) scale(0.95)",
                                },
                                "100%": {
                                    opacity: 1,
                                    transform: "translateY(0) scale(1)",
                                },
                            },
                            animation:
                                "ctaEnter 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.55s both",
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: "-100%",
                                width: "100%",
                                height: "100%",
                                background:
                                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)",
                                "@keyframes shimmer": {
                                    "0%": { left: "-100%" },
                                    "100%": { left: "100%" },
                                },
                                animation:
                                    "shimmer 2.5s ease-in-out 1.5s infinite",
                                pointerEvents: "none",
                            },
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            spacing={1}
                            sx={{ py: "clamp(14px, 2vh, 18px)", px: 3 }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: FONTS.display,
                                    fontWeight: 700,
                                    fontSize: "17px",
                                    color: primary,
                                    letterSpacing: "0.1px",
                                }}
                            >
                                Create My App
                            </Typography>
                            <ArrowForwardRoundedIcon
                                sx={{ color: primary, fontSize: 20 }}
                            />
                        </Stack>
                    </Box>

                    {/* Secondary CTAs */}
                    <Stack spacing={0.75} width="100%" maxWidth={340}>
                        <Box
                            role="button"
                            onClick={() =>
                                (window.location.href =
                                    "https://admin.jemloc.com")
                            }
                            sx={{
                                cursor: "pointer",
                                userSelect: "none",
                                display: "flex",
                                alignItems: "center",
                                py: "clamp(6px, 1vh, 10px)",
                                px: 2,
                                borderRadius: "14px",
                                bgcolor: "rgba(255,255,255,0.08)",
                                border: "1px solid rgba(255,255,255,0.1)",
                                backdropFilter: "blur(8px)",
                                transition: "all 0.15s ease",
                                "&:hover": {
                                    bgcolor: "rgba(255,255,255,0.14)",
                                    borderColor: "rgba(255,255,255,0.2)",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: FONTS.body,
                                    fontSize: "13.5px",
                                    color: "rgba(255,255,255,0.55)",
                                    flex: 1,
                                }}
                            >
                                Already have an app?{" "}
                                <Typography
                                    component="span"
                                    sx={{
                                        fontWeight: 600,
                                        color: "rgba(255,255,255,0.9)",
                                    }}
                                >
                                    Sign in
                                </Typography>
                            </Typography>
                            <ArrowForwardRoundedIcon
                                sx={{
                                    color: "rgba(255,255,255,0.35)",
                                    fontSize: 16,
                                    flex: "0 0 auto",
                                }}
                            />
                        </Box>

                        <Box
                            role="button"
                            onClick={() =>
                                (window.location.href = "/affiliate")
                            }
                            sx={{
                                cursor: "pointer",
                                userSelect: "none",
                                display: "flex",
                                alignItems: "center",
                                py: "clamp(6px, 1vh, 10px)",
                                px: 2,
                                borderRadius: "14px",
                                bgcolor: "rgba(255,255,255,0.08)",
                                border: "1px solid rgba(255,255,255,0.1)",
                                backdropFilter: "blur(8px)",
                                transition: "all 0.15s ease",
                                "&:hover": {
                                    bgcolor: "rgba(255,255,255,0.14)",
                                    borderColor: "rgba(255,255,255,0.2)",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: FONTS.body,
                                    fontSize: "13.5px",
                                    color: "rgba(255,255,255,0.55)",
                                    flex: 1,
                                }}
                            >
                                Earn with Jemloc?{" "}
                                <Typography
                                    component="span"
                                    sx={{
                                        fontWeight: 600,
                                        color: "rgba(255,255,255,0.9)",
                                    }}
                                >
                                    Become a Partner
                                </Typography>
                            </Typography>
                            <ArrowForwardRoundedIcon
                                sx={{
                                    color: "rgba(255,255,255,0.35)",
                                    fontSize: 16,
                                    flex: "0 0 auto",
                                }}
                            />
                        </Box>
                    </Stack>

                    <Typography
                        textAlign="center"
                        onClick={() =>
                            (window.location.href =
                                "https://jemloc.com/terms")
                        }
                        sx={{
                            fontFamily: FONTS.body,
                            fontSize: "11px",
                            color: "rgba(255,255,255,0.25)",
                            cursor: "pointer",
                        }}
                    >
                        Terms & Conditions
                    </Typography>
                </Stack>
            </Stack>
        </OnboardingShell>
    );
};

export default StartScreen;
